import { get } from "./api";
import {
  SalesStatistic,
  CampaignProductsStatistics,
  CampaignStatistics,
  OnShelfAvailabilityStatistics,
  ProductProfitStatistics,
  ProductWasteStatistics,
  ProfitStatistics,
  StoreWasteStatistics,
  UserStatistics,
  UserStoreStatistics,
  CustomerFeedbackStatistics,
  ProductSaleStatistic,
  DatedSalesStatistic,
  Refund
} from "../models/Statistics";
import { err, ok, Result } from "../result";
import { ProblemDetails } from "../models/Http";
import moment from "moment";
import { CampaignTargetUserType } from "../models/Campaign";

export const getAverageReceiptStatistics = async (
  from: Date,
  to: Date,
  storeIds: string[]
): Promise<Result<ProblemDetails, ReadonlyArray<SalesStatistic>>> => {
  let additionalQuery = "";

  storeIds.forEach(val => {
    additionalQuery = `${additionalQuery}&storeIds=${val}`;
  });

  const response = await get(
    `/v1/statistics/sales?periodFrom=${moment(from).format(
      "YYYY-MM-DD"
    )}&periodTo=${moment(to).format("YYYY-MM-DD")}${additionalQuery}`
  );
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<SalesStatistic>);
  }

  return err(content as ProblemDetails);
};

export const getProductSalesStatistics = async (
  from: Date,
  to: Date,
  storeIds: string[] = []
): Promise<Result<ProblemDetails, ReadonlyArray<ProductSaleStatistic>>> => {
  let additionalQuery = "";

  storeIds.forEach(val => {
    additionalQuery = `${additionalQuery}&storeIds=${val}`;
  });

  const response = await get(
    `/v1/statistics/productsales?periodFrom=${moment(from).format(
      "YYYY-MM-DD"
    )}&periodTo=${moment(to).format("YYYY-MM-DD")}${additionalQuery}`
  );
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<ProductSaleStatistic>);
  }

  return err(content as ProblemDetails);
};

export const getAverageDatedReceiptStatistics = async (
  from: Date,
  to: Date,
  storeIds: string[]
): Promise<Result<ProblemDetails, ReadonlyArray<DatedSalesStatistic>>> => {
  let additionalQuery = "";

  storeIds.forEach(val => {
    additionalQuery = `${additionalQuery}&storeIds=${val}`;
  });

  const response = await get(
    `/v1/statistics/dated-sales?periodFrom=${moment(from).format(
      "YYYY-MM-DD HH:mm:ss"
    )}&periodTo=${moment(to).format("YYYY-MM-DD HH:mm:ss")}${additionalQuery}`
  );
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<DatedSalesStatistic>);
  }

  throw err(content as ProblemDetails);
};

export const getOnShelfAvailabilityStatistics = async (
  from: Date,
  to: Date
): Promise<
  Result<ProblemDetails, ReadonlyArray<OnShelfAvailabilityStatistics>>
> => {
  const response = await get(
    `/v1/statistics/onshelfavailability?periodFrom=${moment(from).format(
      "YYYY-MM-DD"
    )}&periodTo=${moment(to).format("YYYY-MM-DD")}`
  );
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<OnShelfAvailabilityStatistics>);
  }

  return err(content as ProblemDetails);
};

export const getProfitStatistics = async (
  from: Date,
  to: Date
): Promise<Result<ProblemDetails, ReadonlyArray<ProfitStatistics>>> => {
  const response = await get(
    `/v1/statistics/profit?periodFrom=${moment(from).format(
      "YYYY-MM-DD"
    )}&periodTo=${moment(to).format("YYYY-MM-DD")}`
  );
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<ProfitStatistics>);
  }

  return err(content as ProblemDetails);
};

export const getWasteStatistics = async (
  from: Date,
  to: Date
): Promise<Result<ProblemDetails, ReadonlyArray<StoreWasteStatistics>>> => {
  const response = await get(
    `/v1/statistics/waste?periodFrom=${moment(from).format(
      "YYYY-MM-DD"
    )}&periodTo=${moment(to).format("YYYY-MM-DD")}`
  );
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<StoreWasteStatistics>);
  }

  return err(content as ProblemDetails);
};

export const getProductWasteStatistics = async (
  from: Date,
  to: Date,
  storeId: string
): Promise<Result<ProblemDetails, ReadonlyArray<ProductWasteStatistics>>> => {
  const response = await get(
    `/v1/statistics/productwaste?storeId=${storeId}&periodFrom=${moment(
      from
    ).format("YYYY-MM-DD")}&periodTo=${moment(to).format("YYYY-MM-DD")}`
  );
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<ProductWasteStatistics>);
  }

  return err(content as ProblemDetails);
};

export const getProductProfitStatistics = async (
  from: Date,
  to: Date,
  storeId: string
): Promise<Result<ProblemDetails, ReadonlyArray<ProductProfitStatistics>>> => {
  const response = await get(
    `/v1/statistics/productprofit?storeId=${storeId}&periodFrom=${moment(
      from
    ).format("YYYY-MM-DD")}&periodTo=${moment(to).format("YYYY-MM-DD")}`
  );
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<ProductProfitStatistics>);
  }

  return err(content as ProblemDetails);
};

export const getCampaignStatistics = async (
  campaignDate: Date
): Promise<Result<ProblemDetails, ReadonlyArray<CampaignStatistics>>> => {
  const response = await get(
    `/v1/statistics/campaign?campaignDate=${moment(campaignDate).format(
      "YYYY-MM-DD"
    )}`
  );
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<CampaignStatistics>);
  }

  return err(content as ProblemDetails);
};

export const getCampaignProductsStatistics = async (
  from: Date,
  to: Date,
  campaignId: string,
  targetUserType: CampaignTargetUserType,
  compare: boolean
): Promise<
  Result<ProblemDetails, ReadonlyArray<CampaignProductsStatistics>>
> => {
  const response = await get(
    `/v1/statistics/campaign/${campaignId}?periodFrom=${moment(from).format(
      "YYYY-MM-DD"
    )}&periodTo=${moment(to).format(
      "YYYY-MM-DD"
    )}&targetUserType=${targetUserType}&compare=${compare}`
  );
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<CampaignProductsStatistics>);
  }

  return err(content as ProblemDetails);
};

export const getUserStatistics = async (
  from: Date,
  to: Date
): Promise<Result<ProblemDetails, ReadonlyArray<UserStatistics>>> => {
  const response = await get(
    `/v1/statistics/user?periodFrom=${moment(from).format(
      "YYYY-MM-DD"
    )}&periodTo=${moment(to).format("YYYY-MM-DD")}`
  );
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<UserStatistics>);
  }

  return err(content as ProblemDetails);
};

export const getUserStatisticsForStore = async (
  from: Date,
  to: Date,
  storeId: string,
  ageFrom: number,
  ageTo: number
): Promise<Result<ProblemDetails, ReadonlyArray<UserStoreStatistics>>> => {
  const response = await get(
    `/v1/statistics/user/${storeId}?periodFrom=${moment(from).format(
      "YYYY-MM-DD"
    )}&periodTo=${moment(to).format(
      "YYYY-MM-DD"
    )}&ageFrom=${ageFrom}&ageTo=${ageTo}`
  );
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<UserStoreStatistics>);
  }

  return err(content as ProblemDetails);
};

export const getCustomerFeedback = async (
  from: Date,
  to: Date
): Promise<
  Result<ProblemDetails, ReadonlyArray<CustomerFeedbackStatistics>>
> => {
  const response = await get(
    `/v1/statistics/feedback?periodFrom=${moment(from).format(
      "YYYY-MM-DD"
    )}&periodTo=${moment(to).format("YYYY-MM-DD")}`
  );
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<CustomerFeedbackStatistics>);
  }

  return err(content as ProblemDetails);
};

export const getRefunds = async (
  from: Date,
  to: Date,
  storeIds: string[] = []
): Promise<Result<ProblemDetails, ReadonlyArray<Refund>>> => {
  let additionalQuery = "";

  storeIds.forEach(val => {
    additionalQuery = `${additionalQuery}&storeIds=${val}`;
  });

  const response = await get(
    `/v1/statistics/refunds?periodFrom=${moment(from).format(
      "YYYY-MM-DD HH:mm:ss"
    )}&periodTo=${moment(to).format("YYYY-MM-DD HH:mm:ss")}${additionalQuery}`
  );
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<Refund>);
  }

  return err(content as ProblemDetails);
};
